import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Close as CloseIcon } from '@mui/icons-material';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import TipsAndUpdatesOutlinedIcon from '@mui/icons-material/TipsAndUpdatesOutlined';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../utils/axios';

export interface DonViNhaThauOption {
  id: number;
  code: string;
  name: string;
  short_name: string | null;
  Don_vi_cap: number;
}

export interface SendToContractorsAfterPublishModalProps {
  open: boolean;
  documentId: number | null;
  /** Mã nhà thầu đã chọn sẵn trên form tạo mới (nếu có) */
  initialSelectedCodes: string[];
  /** Bỏ qua, đóng dialog hoặc Xác nhận — luôn gọi với danh sách mã đã chọn (có thể rỗng) */
  onFinish: (selectedCodes: string[]) => void | Promise<void>;
}

const HEADER_GRADIENT = 'linear-gradient(135deg, #0d4d24 0%, #086428 42%, #0a7d38 100%)';
const CONTENT_BG = 'linear-gradient(180deg, #f0faf3 0%, #ffffff 45%, #fafcfa 100%)';

/**
 * Modal sau khi tạo mới văn bản đi: hỏi có gửi cho nhà thầu nào sau khi ban hành không,
 * cho phép chọn nhiều nhà thầu giống Autocomplete trên màn tạo mới.
 */
const SendToContractorsAfterPublishModal: React.FC<SendToContractorsAfterPublishModalProps> = ({
  open,
  documentId,
  initialSelectedCodes,
  onFinish,
}) => {
  const [donViList, setDonViList] = useState<DonViNhaThauOption[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState<DonViNhaThauOption[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const canConfirm = selected.length > 0;

  useEffect(() => {
    if (!open) return;

    setSelected([]);
    const fetchDonVi = async () => {
      setLoadingList(true);
      try {
        const response = await axiosInstance.get('/donvi', {
          params: { page: 1, page_size: -1 },
        });
        if (response.data.code === 0) {
          const records: DonViNhaThauOption[] = response.data.data.records || [];
          setDonViList(records);
          const contractors = records.filter((dv) => dv.Don_vi_cap === 1);
          const pre = contractors.filter((dv) => initialSelectedCodes.includes(dv.code));
          setSelected(pre);
        } else {
          setDonViList([]);
        }
      } catch {
        setDonViList([]);
      } finally {
        setLoadingList(false);
      }
    };

    void fetchDonVi();
  }, [open, documentId, JSON.stringify(initialSelectedCodes)]);

  const contractorOptions = donViList.filter((dv) => dv.Don_vi_cap === 1);

  const finishWith = async (codes: string[]) => {
    setSubmitting(true);
    try {
      await Promise.resolve(onFinish(codes));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = () => void finishWith([]);

  const handleConfirm = () => {
    if (!canConfirm) return;
    void finishWith(selected.map((dv) => dv.code));
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) void finishWith([]);
      }}
      maxWidth="sm"
      fullWidth
      scroll="body"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 18px 48px rgba(8, 100, 40, 0.22), 0 4px 16px rgba(0, 0, 0, 0.06)',
          border: '1px solid rgba(8, 100, 40, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: HEADER_GRADIENT,
          color: 'white',
          py: 2.25,
          px: 2.5,
          pr: 7,
          position: 'relative',
          boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.12)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              backdropFilter: 'blur(6px)',
            }}
          >
            <BusinessCenterOutlinedIcon sx={{ fontSize: 26, color: 'white' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, letterSpacing: '0.02em' }}>
              Gửi văn bản đến nhà thầu sau ban hành
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.92, display: 'block', mt: 0.5 }}>
              Chọn nhà thầu nhận văn bản khi văn bản được ban hành
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={() => !submitting && void finishWith([])}
          disabled={submitting}
          sx={{
            position: 'absolute',
            right: 10,
            top: 10,
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          pt: 2.5,
          pb: 1,
          px: 2.5,
          background: CONTENT_BG,
          borderColor: 'rgba(8, 100, 40, 0.08)',
        }}
      >
        <Box
          sx={{
            p: 1.75,
            mb: 2,
            borderRadius: 2,
            border: '1px solid rgba(8, 100, 40, 0.15)',
            bgcolor: 'rgba(255, 255, 255, 0.85)',
            boxShadow: '0 2px 12px rgba(8, 100, 40, 0.06)',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TipsAndUpdatesOutlinedIcon sx={{ color: '#086428', fontSize: 22, mt: 0.15, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.65, fontWeight: 500 }}>
              Bạn có muốn gửi văn bản này đến nhà thầu nào sau khi được ban hành hay không?
            </Typography>
          </Stack>
        </Box>

        <Autocomplete
          multiple
          loading={loadingList}
          options={contractorOptions}
          getOptionLabel={(option) => `${option.code} - ${option.name}`}
          value={selected}
          onChange={(_, newValue) => setSelected(newValue)}
          disabled={submitting || !documentId}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Nhà thầu ban hành"
              placeholder="Chọn nhà thầu..."
              helperText={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ display: 'block', fontStyle: 'italic', color: '#bf360c', mb: 0.75 }}
                  >
                    Nếu bạn ban hành cho nhà thầu, cần chọn nhà thầu cần ban hành.
                  </Typography>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ display: 'block', fontStyle: 'italic', color: '#bf360c' }}
                  >
                    Lưu ý: nếu bạn không chọn nhà thầu nào thì nhà thầu sẽ không nhận được văn bản của bạn.
                  </Typography>
                </Box>
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': { boxShadow: '0 0 0 1px rgba(8, 100, 40, 0.2)' },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 2px rgba(8, 100, 40, 0.25)',
                  },
                },
                '& .MuiInputBase-root': { height: 'auto' },
                '& .MuiInputBase-input::placeholder': { color: '#2e7d32', opacity: 0.75 },
                '& .MuiFormHelperText-root': { marginTop: 1.25 },
                '& .MuiInputLabel-root.Mui-focused': { color: '#086428' },
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#0d4d24' }}>
                  {option.code}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                  {option.name}
                </Typography>
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.code}
                label={`${option.code}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: 'rgba(8, 100, 40, 0.12)',
                  color: '#064a23',
                  border: '1px solid rgba(8, 100, 40, 0.22)',
                }}
              />
            ))
          }
          isOptionEqualToValue={(option, value) => option.code === value.code}
          noOptionsText={loadingList ? 'Đang tải...' : 'Không tìm thấy đơn vị'}
        />

        {loadingList && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={26} sx={{ color: '#086428' }} />
            <Typography variant="body2" color="text.secondary">
              Đang tải danh sách nhà thầu...
            </Typography>
          </Box>
        )}

        {!loadingList && selected.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#086428', fontWeight: 600 }}>
            Đã chọn {selected.length} nhà thầu — có thể nhấn Xác nhận
          </Typography>
        )}
        {!loadingList && !canConfirm && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'text.secondary' }}>
            Chọn ít nhất một nhà thầu để bật nút Xác nhận, hoặc nhấn Bỏ qua nếu không gửi cho nhà thầu.
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 2,
          gap: 1.5,
          bgcolor: '#f7fbf8',
          borderTop: '1px solid rgba(8, 100, 40, 0.1)',
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleSkip}
          disabled={submitting}
          sx={{
            borderColor: 'rgba(8, 100, 40, 0.35)',
            color: '#0d4d24',
            fontWeight: 600,
            px: 2,
            '&:hover': { borderColor: '#086428', bgcolor: 'rgba(8, 100, 40, 0.06)' },
          }}
        >
          Bỏ qua
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || !canConfirm}
          sx={{
            fontWeight: 700,
            px: 2.5,
            py: 1,
            borderRadius: 2,
            background: canConfirm && !submitting
              ? 'linear-gradient(135deg, #0a8f3d 0%, #086428 55%, #064a23 100%)'
              : undefined,
            backgroundColor: !canConfirm ? 'action.disabledBackground' : undefined,
            boxShadow: canConfirm && !submitting ? '0 4px 14px rgba(8, 100, 40, 0.35)' : 'none',
            '&:hover': {
              background: canConfirm && !submitting
                ? 'linear-gradient(135deg, #0b9d44 0%, #0a7d38 55%, #07552a 100%)'
                : undefined,
              boxShadow: canConfirm && !submitting ? '0 6px 18px rgba(8, 100, 40, 0.4)' : 'none',
            },
            '&.Mui-disabled': {
              color: 'rgba(0,0,0,0.35)',
            },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
              Đang lưu...
            </>
          ) : (
            'Xác nhận'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendToContractorsAfterPublishModal;
